import { useMemo, useState } from 'react'
import { CheckCircle2, Copy, MessageCircle } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { useBranch } from '@/contexts/BranchContext'
import {
  buildBookingWhatsAppMessage,
  formatAppointmentDateTime,
  openWhatsAppBooking,
} from '@/lib/calendar/appointmentWhatsApp'
import { formatE164Display } from '@/lib/phone'

/**
 * @param {object} props
 * @param {import('@/types/api').Appointment[]} props.appointments
 * @param {import('@/types/api').Customer | null} props.customer
 * @param {() => void} props.onDone
 */
export function BookAppointmentSuccessPanel({ appointments, customer, onDone }) {
  const { user } = useAuth()
  const { activeBranch } = useBranch()
  const [copied, setCopied] = useState(false)

  const primary = appointments[0]
  const salonName = user?.company?.name ?? activeBranch?.name ?? 'our salon'
  const branchName = activeBranch?.name ?? null
  const customerPhone = customer?.phone ?? primary?.customer?.phone ?? null

  const message = useMemo(
    () => buildBookingWhatsAppMessage({
      appointments,
      customer,
      salonName,
      branchName,
    }),
    [appointments, branchName, customer, salonName],
  )

  const { datePart, timePart } = primary
    ? formatAppointmentDateTime(primary.starts_at)
    : { datePart: '', timePart: '' }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(message)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  function handleWhatsApp() {
    openWhatsAppBooking({
      phone: customerPhone,
      message,
    })
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden />
          <div>
            <p className="text-sm font-semibold text-emerald-900">
              {appointments.length > 1
                ? `${appointments.length} appointments booked`
                : 'Appointment booked'}
            </p>
            <p className="mt-1 text-xs text-emerald-800">
              Send the confirmation to the guest on WhatsApp when you are ready.
            </p>
          </div>
        </div>

        <div className="rounded-xl border border-border/60 bg-muted/20 p-3 text-sm">
          <p className="font-semibold text-foreground">
            {customer?.name ?? primary?.customer?.name ?? 'Guest'}
          </p>
          {customerPhone ? (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {formatE164Display(customerPhone)}
            </p>
          ) : (
            <p className="mt-0.5 text-xs text-amber-700">
              No phone on file — WhatsApp will open without a pre-selected chat.
            </p>
          )}
          <dl className="mt-3 space-y-1.5 text-xs">
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">Date</dt>
              <dd className="text-right font-medium text-foreground">{datePart}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">Time</dt>
              <dd className="text-right font-medium text-foreground">{timePart}</dd>
            </div>
            {primary?.stylist?.name ? (
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Stylist</dt>
                <dd className="text-right font-medium text-foreground">{primary.stylist.name}</dd>
              </div>
            ) : null}
          </dl>
        </div>

        <div>
          <p className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            WhatsApp message preview
          </p>
          <pre className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded-xl border border-border/60 bg-card p-3 font-sans text-xs leading-relaxed text-foreground">
            {message}
          </pre>
        </div>
      </div>

      <div className="flex flex-col gap-2 border-t border-primary/10 px-4 py-3 sm:flex-row sm:items-center sm:justify-end">
        <Button
          type="button"
          variant="outline"
          className="sm:order-2"
          onClick={onDone}
        >
          Done
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={handleCopy}
        >
          <Copy className="mr-1.5 h-4 w-4" aria-hidden />
          {copied ? 'Copied' : 'Copy message'}
        </Button>
        <Button
          type="button"
          className="bg-[#25D366] text-white hover:bg-[#1ebe57] sm:order-1 sm:mr-auto"
          onClick={handleWhatsApp}
        >
          <MessageCircle className="mr-1.5 h-4 w-4" aria-hidden />
          Send on WhatsApp
        </Button>
      </div>
    </div>
  )
}
